// Common English words to check if input contains real words
const commonWords = new Set([
  // Common connecting words
  'game', 'about', 'with', 'the', 'and', 'in', 'of', 'to', 'from', 'by', 'on', 'at',
  
  // Animals & Creatures
  'cat', 'dog', 'dragon', 'monster', 'wolf', 'bear', 'bird', 'fish', 'rabbit', 'fox',
  'unicorn', 'phoenix', 'griffin', 'beast', 'creature', 'animal', 'pet',
  
  // Fantasy & Mythology
  'magic', 'wizard', 'witch', 'spell', 'potion', 'sword', 'shield', 'armor', 'knight',
  'castle', 'kingdom', 'realm', 'fairy', 'elf', 'dwarf', 'goblin', 'orc', 'troll',
  'myth', 'legend', 'ancient', 'mystical', 'enchanted', 'cursed', 'blessed',
  
  // Sci-Fi
  'space', 'alien', 'robot', 'cyborg', 'android', 'laser', 'ship', 'planet', 'star',
  'galaxy', 'cosmos', 'future', 'tech', 'science', 'sci', 'fi', 'time', 'dimension',
  
  // Game Elements
  'card', 'deck', 'board', 'dice', 'token', 'piece', 'tile', 'hand', 'draw', 'play',
  'roll', 'move', 'turn', 'round', 'score', 'point', 'level', 'rule', 'match',
  
  // Roles & Characters
  'hero', 'villain', 'player', 'warrior', 'mage', 'rogue', 'healer', 'archer',
  'fighter', 'leader', 'master', 'apprentice', 'king', 'queen', 'prince', 'princess',
  
  // Actions & Combat
  'fight', 'battle', 'attack', 'defend', 'cast', 'shoot', 'run', 'jump', 'fly',
  'swim', 'climb', 'explore', 'search', 'hunt', 'chase', 'escape', 'hide',
  
  // Story Elements
  'quest', 'journey', 'adventure', 'story', 'tale', 'mission', 'challenge', 'puzzle',
  'mystery', 'secret', 'treasure', 'artifact', 'relic', 'map', 'path', 'destiny',
  
  // Settings & Locations
  'world', 'land', 'city', 'town', 'village', 'forest', 'mountain', 'cave', 'dungeon',
  'temple', 'shrine', 'island', 'sea', 'ocean', 'river', 'desert', 'jungle',
  
  // Genres
  'fantasy', 'adventure', 'action', 'strategy', 'rpg', 'role', 'playing', 'puzzle',
  'mystery', 'horror', 'comedy', 'drama', 'simulation', 'management', 'building',
  
  // Modern/Urban
  'restaurant', 'shop', 'store', 'cafe', 'school', 'office', 'house', 'building',
  'street', 'car', 'phone', 'computer', 'food', 'cooking', 'business', 'money',
  
  // Emotions & States
  'fun', 'happy', 'sad', 'angry', 'scared', 'brave', 'strong', 'weak', 'fast',
  'slow', 'smart', 'wise', 'crazy', 'wild', 'calm', 'peaceful', 'chaos'
]);

interface ValidationResult {
  isValid: boolean;
  error?: string;
  cleanInput?: string;
  stats?: {
    totalWords: number;
    uniqueWords: number;
    commonWordsUsed: string[];
    qualityScore: number;
  };
}

interface StructureCheck {
  isValid: boolean;
  error?: string;
  cleanInput?: string;
}

// Validate input length and check for repeated characters
function validateInputStructure(input: string): StructureCheck {
  if (!input || typeof input !== 'string') {
    return { isValid: false, error: 'Input must be text' };
  }

  // Remove extra spaces and trim
  const cleanInput = input.replace(/\s+/g, ' ').trim();

  if (cleanInput.length < 7) {
    return { isValid: false, error: 'Input must be at least 7 characters long' };
  }

  // Check for repeated characters
  const chars = cleanInput.toLowerCase().split('');
  const charCount: { [key: string]: number } = {};
  chars.forEach(char => {
    charCount[char] = (charCount[char] || 0) + 1;
  });

  const mostRepeatedChar = Object.entries(charCount)
    .sort(([,a], [,b]) => b - a)[0];

  if (mostRepeatedChar[1] / cleanInput.length > 0.5) {
    return { 
      isValid: false, 
      error: 'Too many repeated characters. Please provide a more varied description.' 
    };
  }

  return { isValid: true, cleanInput };
}

// Analyze text quality and word usage
function analyzeTextQuality(input: string): ValidationResult {
  const words = input.toLowerCase().split(/\s+/);
  
  // Check for minimum unique words
  const uniqueWords = new Set(words);
  if (uniqueWords.size < 2) {
    return { 
      isValid: false, 
      error: 'Please use at least 2 different words to describe your game' 
    };
  }

  // Check if contains some common words
  const foundCommonWords = words.filter(word => commonWords.has(word));
  if (foundCommonWords.length === 0) {
    return {
      isValid: false,
      error: 'Please use some common words to describe your game concept'
    };
  }

  // Calculate rough quality score based on variety of words
  const qualityScore = Math.min(
    (uniqueWords.size / words.length) * 100,
    foundCommonWords.length * 20
  );

  return {
    isValid: true,
    stats: {
      totalWords: words.length,
      uniqueWords: uniqueWords.size,
      commonWordsUsed: foundCommonWords,
      qualityScore
    }
  };
}

// Main validation function that combines all checks
export function validateGameDescription(input: string): ValidationResult {
  // Step 1: Validate structure
  const structureCheck = validateInputStructure(input);
  if (!structureCheck.isValid) {
    return structureCheck;
  }

  // Step 2: Analyze text quality
  const qualityCheck = structureCheck.cleanInput 
    ? analyzeTextQuality(structureCheck.cleanInput)
    : null;

  if (!qualityCheck) {
    return {
      isValid: false,
      error: 'Quality analysis failed'
    };
  }

  return {
    isValid: true,
    cleanInput: structureCheck.cleanInput,
    stats: qualityCheck.stats
  };
} 